/**
 * My Portfolio Section (Leader Dashboard)
 *
 * 리더 포트폴리오 관리 섹션 - 신규 디자인 스타일
 * - 포트폴리오 정보 및 통계
 * - 성과 지표 (ROI, Win Rate, Sharpe Ratio)
 * - 팔로워 수, 복사된 자금 총액
 * - 포트폴리오 설정 관리
 */

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { message, Modal, Slider, Select, InputNumber, Upload } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import type { UploadChangeParam } from 'antd/es/upload';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import BigNumber from 'bignumber.js';

import { useMyPortfolio } from '@app/hooks/copyTrading/useMyPortfolio';
import { updateMyPortfolio, createPortfolio } from '@app/api/copyTrading';
import { CopyRegisterModal } from '../../CopyTradePage/modal/CopyRegisterModal';
import { useToken } from '@app/hooks';
import API from '@utils/api';

const { VITE_APP_API_MEDIA_DEV_URL } = import.meta.env;

import type { CopyPortfolio, CopyPortfolioStats } from '../../../../types/copyTrading';

interface MyPortfolioSectionProps {
  isMobile?: boolean;
}

// Helper functions
const getBase64 = (img: RcFile, callback: (url: string) => void) => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result as string));
  reader.readAsDataURL(img);
};

const beforeUpload = (file: RcFile) => {
  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
  if (!isJpgOrPng) {
    message.error('You can only upload JPG/PNG file!');
  }
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    message.error('Image must smaller than 2MB!');
  }
  return isJpgOrPng && isLt2M;
};

export const MyPortfolioSection: React.FC<MyPortfolioSectionProps> = ({ isMobile = true }) => {
  const { t } = useTranslation();
  const { portfolio, hasPortfolio, isLoading, refetch } = useMyPortfolio();
  const { token } = useToken();

  const [openRegisterModal, setOpenRegisterModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    portfolioName: '',
    description: '',
    profitShareRate: 15,
    minCopyAmount: 100,
    maxFollowers: 500,
    status: 1 as 1 | 2 | 3,
    imageUrl: '' as string | undefined,
  });

  // 이미지 업로드 상태
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>();
  const [imageKey, setImageKey] = useState<string>();
  const [imageLoading, setImageLoading] = useState(false);

  // Stats (MVP에서는 기본값)
  const [stats] = useState<CopyPortfolioStats | null>(null);

  const summary = useMemo(() => ({
    totalFollowers: portfolio?.currentFollowers || 0,
    totalCopiedAmount: 0,
    totalEarningsThisMonth: 0,
    totalEarningsAll: 0,
  }), [portfolio]);

  const statusInfo = useMemo(() => {
    if (!portfolio) return { text: 'N/A', color: '#777986' };
    const statusMap = {
      1: { text: t('activeStatus'), color: '#07d993' },
      2: { text: t('pausedStatus'), color: '#ffc107' },
      3: { text: t('closedStatus'), color: '#ff4d4f' },
    };
    return statusMap[portfolio.status as 1 | 2 | 3] || { text: 'Unknown', color: '#777986' };
  }, [portfolio, t]);

  const followerCapacityPercent = useMemo(() => {
    if (!portfolio) return 0;
    return BigNumber(portfolio.currentFollowers)
      .dividedBy(portfolio.maxFollowers)
      .multipliedBy(100)
      .toNumber();
  }, [portfolio]);

  const handleOpenEditModal = () => {
    if (!portfolio) return;
    setEditForm({
      portfolioName: portfolio.portfolioName,
      description: portfolio.description || '',
      profitShareRate: portfolio.profitShareRate,
      minCopyAmount: portfolio.minCopyAmount,
      maxFollowers: portfolio.maxFollowers,
      status: portfolio.status,
      imageUrl: portfolio.imageUrl,
    });
    // 기존 이미지가 있으면 미리보기 설정
    if (portfolio.imageUrl) {
      const fullImageUrl = portfolio.imageUrl.startsWith('http')
        ? portfolio.imageUrl
        : `${VITE_APP_API_MEDIA_DEV_URL}/${portfolio.imageUrl}`;
      setImagePreviewUrl(fullImageUrl);
      setImageKey(portfolio.imageUrl);
    } else {
      setImagePreviewUrl(undefined);
      setImageKey(undefined);
    }
    setOpenEditModal(true);
  };

  // 이미지 업로드 핸들러
  const handleImageChange = async (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'uploading') {
      setImageLoading(true);
      return;
    }

    setImageLoading(true);

    try {
      // 1. Presigned URL 요청
      const response = await API.post('/upload/presigned-url', token, {
        usage: 'copy-portfolio-image',
        fileExtension: info?.file?.type === 'image/jpeg' ? 'jpg' : 'png',
      });

      if (response.success) {
        // 2. R2에 직접 업로드
        const fileUrl = response.result.url;
        const uploadRes = await fetch(fileUrl, {
          method: 'PUT',
          body: info?.file?.originFileObj,
          headers: { 'Content-Type': info?.file?.type || 'image/png' },
        });

        if (uploadRes.ok) {
          // 3. 업로드 성공 확인
          const fullImageUrl = `${VITE_APP_API_MEDIA_DEV_URL}/${response.result.key}`;
          const checkRes = await fetch(fullImageUrl);

          if (checkRes.ok) {
            const key = response.result.key;
            setImageKey(key);
            getBase64(info.file.originFileObj as RcFile, (url) => {
              setImagePreviewUrl(url);
            });
            message.success(t('imageUploadSuccess') || 'Image uploaded successfully');
          } else {
            message.error(t('imageUploadFailed') || 'Image upload failed');
          }
        }
      }
    } catch (error) {
      console.error('Upload failed', error);
      message.error(t('imageUploadFailed') || 'Failed to upload image');
    } finally {
      setImageLoading(false);
    }
  };

  const handleDeleteImage = () => {
    setImagePreviewUrl(undefined);
    setImageKey(undefined);
  };

  const handleSaveEdit = async () => {
    if (!portfolio) return;
    try {
      const updateData = {
        ...editForm,
        imageUrl: imageKey, // 이미지 키 포함
      };
      const response = await updateMyPortfolio(updateData);
      if (response.success) {
        message.success(t('portfolioUpdated'));
        setOpenEditModal(false);
        await refetch();
      } else {
        message.error(t('updateFailed'));
      }
    } catch (error: any) {
      message.error(t('updateFailed'));
    }
  };

  const handlePause = async () => {
    if (!portfolio) return;
    try {
      const response = await updateMyPortfolio({ status: 2 } as any);
      if (response.success) {
        message.success(t('portfolioPaused'));
        await refetch();
      }
    } catch (error: any) {
      message.error(t('pauseFailed'));
    }
  };

  const handleResume = async () => {
    if (!portfolio) return;
    try {
      const response = await updateMyPortfolio({ status: 1 } as any);
      if (response.success) {
        message.success(t('portfolioResumed'));
        await refetch();
      }
    } catch (error: any) {
      message.error(t('resumeFailed'));
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <Container $isMobile={isMobile}>
        <LoadingText>{t('loading')}</LoadingText>
      </Container>
    );
  }

  // Empty State - 포트폴리오 없음
  if (!hasPortfolio || !portfolio) {
    return (
      <Container $isMobile={isMobile}>
        <CopyRegisterModal open={openRegisterModal} setOpen={setOpenRegisterModal} />
        <EmptyStateCard>
          <EmptyIcon>📊</EmptyIcon>
          <EmptyTitle>{t('noLeaderPortfolio')}</EmptyTitle>
          <EmptyDescription>{t('noLeaderPortfolioDesc')}</EmptyDescription>
          <CreateButton onClick={() => setOpenRegisterModal(true)}>
            {t('createPortfolio')}
          </CreateButton>
        </EmptyStateCard>
      </Container>
    );
  }

  // Portfolio Dashboard
  return (
    <Container $isMobile={isMobile}>
      {/* Header Section */}
      <SectionHeader $isMobile={isMobile}>
        <SectionTitle $isMobile={isMobile}>{t('myPortfolioDashboard')}</SectionTitle>
      </SectionHeader>

      {/* Portfolio Info Card */}
      <PortfolioInfoCard>
        <PortfolioHeader>
          <PortfolioName>{portfolio.portfolioName}</PortfolioName>
          <StatusBadge $color={statusInfo.color}>{statusInfo.text}</StatusBadge>
        </PortfolioHeader>
        {portfolio.description && (
          <PortfolioDescription>{portfolio.description}</PortfolioDescription>
        )}
        <ActionButtons>
          <ActionButton onClick={handleOpenEditModal}>{t('settings')}</ActionButton>
          {portfolio.status === 1 && (
            <ActionButton $variant="warning" onClick={handlePause}>{t('pause')}</ActionButton>
          )}
          {portfolio.status === 2 && (
            <ActionButton $variant="primary" onClick={handleResume}>{t('resume')}</ActionButton>
          )}
        </ActionButtons>
      </PortfolioInfoCard>

      {/* Summary Stats */}
      <StatsGrid>
        <StatCard>
          <StatLabel>{t('totalFollowers')}</StatLabel>
          <StatValue>{summary.totalFollowers}</StatValue>
          <StatSubtext>/ {portfolio.maxFollowers} {t('max')}</StatSubtext>
          <ProgressBar>
            <ProgressFill $percent={followerCapacityPercent} />
          </ProgressBar>
        </StatCard>

        <StatCard>
          <StatLabel>{t('profitShareRate')}</StatLabel>
          <StatValue $color="#07d993">{portfolio.profitShareRate}%</StatValue>
        </StatCard>

        <StatCard>
          <StatLabel>{t('minCopyAmount')}</StatLabel>
          <StatValue>{portfolio.minCopyAmount}</StatValue>
          <StatUnit>USDT</StatUnit>
        </StatCard>

        <StatCard>
          <StatLabel>{t('maxFollowers')}</StatLabel>
          <StatValue>{portfolio.maxFollowers}</StatValue>
        </StatCard>
      </StatsGrid>

      {/* Performance Metrics */}
      <MetricsSection>
        <MetricsSectionTitle>{t('performanceMetrics')}</MetricsSectionTitle>
        <MetricsGrid>
          <MetricCard>
            <MetricLabel>{t('totalTrades')}</MetricLabel>
            <MetricValue>{stats?.totalTrades || 0}</MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>{t('winRate')}</MetricLabel>
            <MetricValue $color="#07d993">{BigNumber(stats?.winRate || 0).toFixed(1)}%</MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>{t('roi30d')}</MetricLabel>
            <MetricValue $color="#07d993">{BigNumber(stats?.roi30d || 0).toFixed(1)}%</MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>{t('maxDrawdown')}</MetricLabel>
            <MetricValue $color="#ff4d4f">{BigNumber(stats?.maxDrawdown || 0).toFixed(1)}%</MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>{t('sharpeRatio')}</MetricLabel>
            <MetricValue>{BigNumber(stats?.sharpeRatio || 0).toFixed(2)}</MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>{t('roiAll')}</MetricLabel>
            <MetricValue $color="#07d993">{BigNumber(stats?.roiAll || 0).toFixed(1)}%</MetricValue>
          </MetricCard>
        </MetricsGrid>
      </MetricsSection>

      {/* Edit Modal */}
      <StyledModal
        open={openEditModal}
        width={424}
        centered={true}
        closable={false}
        onCancel={() => setOpenEditModal(false)}
        footer={null}
        maskClosable={false}
      >
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>{t('editPortfolio')}</ModalTitle>
            <CloseButton onClick={() => setOpenEditModal(false)}>
              <CloseIcon
                src="/assets/newHome/copy-trading/close-icon-follow.svg"
                alt="Close"
              />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            {/* Portfolio Image */}
            <FormSection>
              <FormLabel>{t('portfolioImage')}</FormLabel>
              <ImageSection>
                <ImageWrapper>
                  {imagePreviewUrl ? (
                    <PortfolioImage src={imagePreviewUrl} alt="Portfolio" />
                  ) : (
                    <Upload
                      name="avatar"
                      listType="picture-circle"
                      className="avatar-uploader"
                      showUploadList={false}
                      beforeUpload={beforeUpload}
                      onChange={handleImageChange}
                    >
                      <UploadButton>
                        {imageLoading ? <LoadingOutlined /> : <PlusOutlined />}
                      </UploadButton>
                    </Upload>
                  )}
                </ImageWrapper>
                {imagePreviewUrl && (
                  <ImageActions>
                    <Upload
                      name="avatar"
                      showUploadList={false}
                      beforeUpload={beforeUpload}
                      onChange={handleImageChange}
                    >
                      <UpdateImageButton>
                        {imageLoading ? <LoadingOutlined /> : t('update')}
                      </UpdateImageButton>
                    </Upload>
                    <DeleteImageButton onClick={handleDeleteImage}>
                      {t('delete')}
                    </DeleteImageButton>
                  </ImageActions>
                )}
              </ImageSection>
            </FormSection>

            <FormSection>
              <FormLabel>{t('portfolioName')}</FormLabel>
              <StyledInput
                value={editForm.portfolioName}
                onChange={(e) => setEditForm({ ...editForm, portfolioName: e.target.value })}
                placeholder={t('portfolioName')}
              />
            </FormSection>

            <FormSection>
              <FormLabel>{t('description')}</FormLabel>
              <StyledTextarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder={t('portfolioDescription')}
                rows={3}
              />
            </FormSection>

            <FormSection>
              <FormLabel>{t('profitShareRate')}</FormLabel>
              <SliderContainer>
                <div
                  style={{
                    position: 'relative',
                    boxSizing: 'border-box',
                    padding: '12px 17px 0 14px',
                    marginBottom: '8px',
                    width: '100%',
                  }}
                >
                  <Slider
                    min={0}
                    max={30}
                    value={editForm.profitShareRate}
                    onChange={(value) => setEditForm({ ...editForm, profitShareRate: value })}
                    marks={{
                      0: {
                        label: (
                          <div style={{ fontSize: 12, color: '#B3B5BD', fontFamily: 'NanumSquareRound', position: 'relative', left: 'calc(50% - 4px)', top: '4px' }}>
                            0%
                          </div>
                        ),
                      },
                      10: { label: <strong style={{ fontSize: 11 }}>{''}</strong> },
                      20: { label: <strong style={{ fontSize: 11 }}>{''}</strong> },
                      30: {
                        label: (
                          <div style={{ fontSize: 12, color: '#B3B5BD', fontFamily: 'NanumSquareRound', position: 'relative', right: 'calc(50% - 4px)', top: '4px' }}>
                            30%
                          </div>
                        ),
                      },
                    }}
                    styles={{
                      rail: { background: '#2A2C2E', height: 3 },
                      track: { background: '#07D993', height: 3 },
                      handle: { border: '1px solid #07D993', background: '#07D993' },
                    }}
                  />
                </div>
                <SliderCurrentValue>{editForm.profitShareRate}%</SliderCurrentValue>
              </SliderContainer>
            </FormSection>

            <FormSection>
              <FormLabel>{t('minimumCopyAmount')}</FormLabel>
              <StyledSelect
                value={editForm.minCopyAmount}
                onChange={(value) => setEditForm({ ...editForm, minCopyAmount: value as number })}
              >
                <Select.Option value={100}>100 USDT</Select.Option>
                <Select.Option value={200}>200 USDT</Select.Option>
                <Select.Option value={500}>500 USDT</Select.Option>
                <Select.Option value={1000}>1000 USDT</Select.Option>
                <Select.Option value={5000}>5000 USDT</Select.Option>
              </StyledSelect>
            </FormSection>

            <FormSection>
              <FormLabel>{t('maxFollowers')}</FormLabel>
              <StyledInputNumber
                min={1}
                max={5000}
                value={editForm.maxFollowers}
                disabled
              />
              <HelperText>{t('maxFollowersCannotBeChanged')}</HelperText>
            </FormSection>
          </ModalBody>

          <ModalFooter>
            <CancelButton onClick={() => setOpenEditModal(false)}>{t('cancel')}</CancelButton>
            <SaveButton onClick={handleSaveEdit}>{t('save')}</SaveButton>
          </ModalFooter>
        </ModalContainer>
      </StyledModal>
    </Container>
  );
};

// Styled Components
const Container = styled.section<{ $isMobile?: boolean }>`
  width: 100%;
  max-width: ${props => props.$isMobile ? '100%' : '1728px'};
  margin: 0 auto;
  margin-top: 32px;
  padding: ${props => props.$isMobile ? '0 16px' : '0 144px'};
  display: flex;
  flex-direction: column;
  gap: ${props => props.$isMobile ? '16px' : '32px'};
`;

const LoadingText = styled.div`
  width: 100%;
  padding: 60px 0;
  text-align: center;
  color: #ffffff;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
`;

const SectionHeader = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.$isMobile ? '16px' : '46px'};
`;

const SectionTitle = styled.h2<{ $isMobile?: boolean }>`
  font-family: 'Aggro', sans-serif;
  font-size: ${props => props.$isMobile ? '20px' : '28px'};
  font-weight: 500;
  color: #ffffff;
  letter-spacing: ${props => props.$isMobile ? '-0.4px' : '-0.56px'};
  line-height: ${props => props.$isMobile ? '28px' : '1.2'};
  flex-shrink: 0;
`;

const PortfolioInfoCard = styled.div`
  background: #0c2323;
  border-radius: 16px;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PortfolioHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PortfolioName = styled.h3`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const StatusBadge = styled.span<{ $color: string }>`
  padding: 4px 12px;
  border-radius: 6px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${props => props.$color};
  background: ${props => `${props.$color}20`};
`;

const PortfolioDescription = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #b3b5bd;
  margin: 0;
  line-height: 1.5;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'warning' }>`
  flex: 1;
  padding: 12px;
  border-radius: 8px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${props =>
    props.$variant === 'primary' ? '#07d993' :
    props.$variant === 'warning' ? '#ffc107' :
    'transparent'
  };
  color: ${props =>
    props.$variant === 'primary' ? '#0d1117' :
    props.$variant === 'warning' ? '#0d1117' :
    '#ffffff'
  };
  border: ${props =>
    !props.$variant ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'
  };

  &:hover {
    opacity: 0.8;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const StatCard = styled.div`
  background: #0c2323;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  color: #b3b5bd;
`;

const StatValue = styled.div<{ $color?: string }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: ${props => props.$color || '#ffffff'};
`;

const StatSubtext = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  color: #777986;
`;

const StatUnit = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #b3b5bd;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  margin-top: 8px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $percent: number }>`
  width: ${props => Math.min(props.$percent, 100)}%;
  height: 100%;
  background: linear-gradient(90deg, #07d993 0%, #ffcc0d 100%);
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const MetricsSection = styled.div`
  background: #0c2323;
  border-radius: 16px;
  padding: 20px 16px;
`;

const MetricsSectionTitle = styled.h3`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 16px 0;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const MetricCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetricLabel = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 11px;
  color: #777986;
`;

const MetricValue = styled.div<{ $color?: string }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.$color || '#ffffff'};
`;

const EmptyStateCard = styled.div`
  background: #0c2323;
  border-radius: 24px;
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
`;

const EmptyTitle = styled.h3`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const EmptyDescription = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #b3b5bd;
  margin: 0 0 16px 0;
`;

const CreateButton = styled.button`
  padding: 16px 32px;
  background: #07d993;
  border: none;
  border-radius: 12px;
  color: #0d1117;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #06c885;
  }
`;

// Modal Styles
const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: #0c2323;
    border-radius: 16px;
    padding: 0;
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 0;
  }
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 40px 35px;
`;

const ModalTitle = styled.h2`
  font-family: 'Aggro', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
  letter-spacing: -0.36px;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 20px;
  height: 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 35px;
  padding-bottom: 24px;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 30px 35px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Image Upload Styles
const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const ImageWrapper = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);

  .avatar-uploader {
    .ant-upload {
      width: 100px !important;
      height: 100px !important;
      border-radius: 50% !important;
      background: rgba(255, 255, 255, 0.05) !important;
      border: 1px dashed rgba(255, 255, 255, 0.2) !important;
    }
  }
`;

const PortfolioImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UploadButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #b3b5bd;
  font-size: 24px;
`;

const ImageActions = styled.div`
  display: flex;
  gap: 12px;
`;

const UpdateImageButton = styled.button`
  padding: 8px 16px;
  background: rgba(7, 217, 147, 0.15);
  border: none;
  border-radius: 8px;
  color: #07d993;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(7, 217, 147, 0.25);
  }
`;

const DeleteImageButton = styled.button`
  padding: 8px 16px;
  background: rgba(255, 77, 79, 0.15);
  border: none;
  border-radius: 8px;
  color: #ff4d4f;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 77, 79, 0.25);
  }
`;

const FormLabel = styled.label`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const HelperText = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #777986;
  letter-spacing: -0.24px;
`;

const StyledInput = styled.input`
  height: 50px;
  padding: 10px 20px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: #ffffff;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.28px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #b3b5bd;
  }

  &:focus {
    border-color: #07d993;
  }
`;

const StyledTextarea = styled.textarea`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: #b3b5bd;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.28px;
  resize: none;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #b3b5bd;
  }

  &:focus {
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SliderCurrentValue = styled.div`
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #07d993;
  letter-spacing: -0.32px;
  text-align: center;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  height: 50px;

  .ant-select-selector {
    height: 50px !important;
    padding: 10px 20px !important;
    background: transparent !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 12px !important;
    color: #ffffff !important;
    font-family: 'NanumSquareRound', sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    letter-spacing: -0.28px !important;
  }

  .ant-select-selection-item {
    line-height: 30px !important;
    color: #ffffff !important;
  }

  .ant-select-arrow {
    color: #ffffff !important;
  }
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  height: 50px;

  .ant-input-number-input {
    height: 50px !important;
    padding: 10px 20px !important;
    background: transparent !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 12px !important;
    color: #ffffff !important;
    font-family: 'NanumSquareRound', sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    letter-spacing: -0.28px !important;
  }

  .ant-input-number-handler-wrap {
    display: none;
  }

  &:focus-within .ant-input-number-input {
    border-color: #07d993 !important;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  height: 48px;
  padding: 20px 40px;
  background: transparent;
  border: 1px solid #353535;
  border-radius: 12px;
  color: #ffffff;
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const SaveButton = styled.button`
  flex: 1;
  height: 48px;
  padding: 20px 40px;
  background: #07d993;
  border: none;
  border-radius: 12px;
  color: #0d1117;
  font-family: 'NanumSquareRound Bold', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #06c885;
  }
`;

export default MyPortfolioSection;
